/* Separate element keeps legacy slideshow-component behavior unchanged. */
if (!customElements.get('wolff-slideshow')) {
  customElements.define('wolff-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.wolff-hero__slide')];
      if (!this.slides.length) return;
      this.dots = [...this.querySelectorAll('[data-slide]')];
      this.pauseButton = this.querySelector('[data-pause]');
      this.status = this.querySelector('[data-status]');
      this.index = 0;
      this.paused = this.dataset.autoplay !== 'true';
      this.focused = this.editorSelected = false;
      this.offscreen = true;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.events = new AbortController();
      const listen = (target, name, callback) => target.addEventListener(name, callback, { signal: this.events.signal });
      this.collections = this.dataset.collections ? document.getElementById(this.dataset.collections) : null;
      this.collectionPanels = this.collections ? [...this.collections.querySelectorAll('[data-collection-slide]')] : [];
      if (this.collections) {
        // Do not replace a product link while someone is using it with the keyboard.
        listen(this.collections, 'focusin', () => { this.collectionFocused = true; this.schedule(); });
        listen(this.collections, 'focusout', () => queueMicrotask(() => {
          this.collectionFocused = this.collections.contains(document.activeElement);
          this.schedule();
        }));
      }
      listen(this.querySelector('[data-prev]') || this, 'click', event => {
        if (event.target.closest('[data-prev]')) this.show(this.index - 1, true);
      });
      listen(this.querySelector('[data-next]') || this, 'click', event => {
        if (event.target.closest('[data-next]')) this.show(this.index + 1, true);
      });
      this.dots.forEach(dot => listen(dot, 'click', () => this.show(Number(dot.dataset.slide), true)));
      if (this.pauseButton) listen(this.pauseButton, 'click', () => {
        this.paused = !this.paused;
        this.updatePauseButton();
        this.schedule();
      });
      listen(this, 'focusin', () => {
        this.focused = this.querySelector(':focus-visible') !== null;
        this.schedule();
      });
      listen(this, 'focusout', () => queueMicrotask(() => {
        this.focused = this.querySelector(':focus-visible') !== null;
        this.schedule();
      }));
      listen(this, 'keydown', event => {
        if (!event.target.closest('.wolff-hero__controls')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowLeft' ? -1 : 1), true);
        }
      });
      listen(this, 'pointerdown', event => {
        this.swipe = null;
        if (event.pointerType !== 'mouse' && !event.target.closest('a, button')) {
          this.swipe = { x: event.clientX, y: event.clientY };
        }
      });
      listen(this, 'pointerup', event => {
        if (!this.swipe) return;
        const dx = event.clientX - this.swipe.x;
        const dy = event.clientY - this.swipe.y;
        this.swipe = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) this.show(this.index + (dx < 0 ? 1 : -1), true);
      });
      listen(this, 'pointercancel', () => { this.swipe = null; });
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.motion, 'change', () => { this.updatePauseButton(); this.schedule(); });
      listen(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (index < 0) return;
        this.editorSelected = true;
        this.show(index);
      });
      listen(document, 'shopify:block:deselect', event => {
        if (!this.contains(event.target)) return;
        this.editorSelected = false;
        this.schedule();
      });
      this.observer = new IntersectionObserver(entries => {
        this.offscreen = !entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.classList.add('is-ready');
      this.show(0);
      this.updatePauseButton();
    }

    show(index, announce = false) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (announce && this.status) this.status.textContent = this.slides[this.index].getAttribute('aria-label');
      this.syncCollection();
      this.schedule();
    }

    syncCollection() {
      if (!this.collections || !this.collectionPanels.length) return;
      // Missing/empty collections retain the first populated panel rather than an empty gap.
      const panel = this.collectionPanels.find(item => Number(item.dataset.collectionSlide) === this.index) || this.collectionPanels[0];
      this.collectionPanels.forEach(item => {
        const active = item === panel;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', String(!active));
        item.inert = !active;
      });
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || this.slides.length < 2 || this.paused || this.focused || this.collectionFocused ||
          this.offscreen || this.editorSelected || document.hidden || this.motion.matches) return;
      this.timer = setTimeout(() => this.show(this.index + 1), Math.max(3, Number(this.dataset.speed) || 7) * 1000);
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      // Reduced motion disables autoplay, including explicit play attempts.
      const paused = this.paused || this.motion.matches;
      this.pauseButton.setAttribute('aria-label', paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
      this.pauseButton.querySelector('[data-pause-icon]').textContent = paused ? '\u25B6' : '\u23F8';
      this.pauseButton.disabled = this.motion.matches;
      this.pauseButton.style.opacity = this.motion.matches ? '0.5' : '';
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.events?.abort();
      this.observer?.disconnect();
      this.classList.remove('is-ready');
    }
  });
}
